document.addEventListener("DOMContentLoaded", function () {
  initDemographicCharts();
});

/**
 * Main initialization function for demographic charts
 * HTML Element IDs used:
 * - googleChartRacialDemographics: Displays racial demographics as a stacked bar
 * - googleChartMedianIncome: Displays median income comparison line chart
 */
function initDemographicCharts() {
  google.charts.load("current", { packages: ["corechart", "bar"] });
  google.charts.setOnLoadCallback(() => {
    drawRacialDemographicsChart();
    drawMedianIncomeChart();
    drawPopulationGrowthChart();
    drawWorkforceStatisticsCharts();
    
    window.addEventListener('resize', handleResize);
  });

  /**
   * Handles window resize events by redrawing all charts
   */
  function handleResize() {
    clearTimeout(window.resizeTimer);
    window.resizeTimer = setTimeout(() => {
      drawRacialDemographicsChart();
      drawMedianIncomeChart();
      drawPopulationGrowthChart();
      drawWorkforceStatisticsCharts();
    }, 250);
  }

  /**
   * Creates a horizontal stacked bar chart for racial demographics
   * HTML Element ID used:
   * - googleChartRacialDemographics: Displays racial demographics
   */
  function drawRacialDemographicsChart() {
    // Check if racial data exists
    if (!apiData.flourishDemographic || !apiData.flourishDemographic.byRace || !apiData.flourishDemographic.byRace.data || !apiData.flourishDemographic.byRace.data.data) {
      return;
    }
    
    // Get data from the API
    const racialData = apiData.flourishDemographic.byRace.data.data;
    
    const labels = racialData[0].slice(1); // Skip first header column
    const values = racialData[1].slice(1).map(val => parseFloat(val)); // Values for each race group
    
    // Create data table for the chart
    const dataTable = new google.visualization.DataTable();
    dataTable.addColumn('string', 'Category');
    
    // Add columns for each racial group
    labels.forEach(label => {
      dataTable.addColumn('number', label);
    });
    
    // Add a single row with all the values
    const rowData = ['Demographics', ...values];
    dataTable.addRow(rowData);
    
    const options = {
      isStacked: true,
      height: 100,
      legend: { position: 'top', maxLines: 3 },
      hAxis: {
        minValue: 0,
        ticks: [0, 20, 40, 60, 80, 100],
        textPosition: 'out'
      },
      chartArea: {
        left: 0,
        right: 0,
        top: 50,
        bottom: 20,
        width: "80%",
        height: "60%"
      },
      // Match colors to the example image
      colors: ['#2e3192', '#E07271', '#71C889', '#9955BB', '#FFCC44'],
      animation: {
        duration: 1000,
        easing: 'out',
        startup: true
      },
      // Remove axes lines and grid
      vAxis: {
        textPosition: 'none',
        gridlines: {
          color: 'transparent'
        }
      },
      // Remove title
      titlePosition: 'none'
    };
    
    const chartElement = document.getElementById('googleChartRacialDemographics');
    if (chartElement) {
      const chart = new google.visualization.BarChart(chartElement);
      chart.draw(dataTable, options);
    }
  }

  /**
   * Creates a line chart comparing area code median income with national median income
   * HTML Element ID used:
   * - googleChartMedianIncome: Displays median income over time
   */
  function drawMedianIncomeChart() {
    // Check if income data exists
    if (!apiData.flourishDemographic || !apiData.flourishDemographic.byMedianIncome || !apiData.flourishDemographic.byMedianIncome.data || !apiData.flourishDemographic.byMedianIncome.data.data) {
      return;
    }
    
    // Get data from the API
    const incomeData = apiData.flourishDemographic.byMedianIncome.data.data;
    
    // Create data table for the chart
    const dataTable = new google.visualization.DataTable();
    dataTable.addColumn('string', 'Year');
    
    let areaCodeName = '';
    try {
      if (apiData.map && apiData.map.data && apiData.map.data.regions_map && apiData.map.data.regions_map.length > 2) {
        areaCodeName = apiData.map.data.regions_map[2];
      } else if (apiData.map && apiData.map.data && apiData.map.data.areaCode) {
        areaCodeName = apiData.map.data.areaCode;
      } else {
        const pathParts = window.location.pathname.split('/');
        for (let i = 0; i < pathParts.length; i++) {
          if (pathParts[i].match(/^\d{3}$/)) {
            areaCodeName = pathParts[i];
            break;
          }
        }
      }
    } catch (error) { // eslint-disable-line no-unused-vars
      areaCodeName = "Area";
    }
    
    dataTable.addColumn('number', `${areaCodeName} Area Code Median Income`);
    dataTable.addColumn('number', 'National Median Income');
    
    incomeData.slice(1).forEach(row => {
      const year = row[0];
      const areaCodeValue = parseFloat(row[2].replace(/,/g, ''));
      const nationalValue = parseFloat(row[3].replace(/,/g, ''));
      
      dataTable.addRow([
        year,
        areaCodeValue, 
        nationalValue
      ]);
    });
    
    const formatDollar = new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      maximumFractionDigits: 0
    });
    
    // Check if we're on a mobile device
    const isMobile = window.innerWidth < 768;
    
    // Adjust font sizes for mobile
    const legendFontSize = isMobile ? 10 : 12;
    const axisFontSize = isMobile ? 9 : 12;
    
    // Use compact format for money on mobile (10K instead of 10,000)
    const moneyFormat = isMobile ? '$#K' : '$#,###';
    
    const options = {
      curveType: 'function',
      height: 450,
      width: '100%',
      legend: { 
        position: 'top',
        alignment: 'start',
        maxLines: 5,  
        textStyle: {
          fontSize: legendFontSize
        }
      },
      chartArea: {
        left: 60,
        right: 0, 
        top: 90,
        bottom: 80,
        width: "100%",
        height: "65%"
      },
      hAxis: {
        title: 'Year',
        slantedText: true,
        slantedTextAngle: 45,
        textStyle: {
          fontSize: 12
        },
        gridlines: {
          color: 'transparent'
        }
      },
      vAxis: {
        format: moneyFormat,
        minValue: 0,
        viewWindow: { min: 0 },
        textStyle: {
          fontSize: axisFontSize
        },
        gridlines: {
          color: 'transparent'
        },
        textPosition: 'out'
      },
      colors: ['#2e3192', '#E24C4B'],
      lineWidth: 3,
      pointSize: 5,
      animation: {
        duration: 1000,
        easing: 'out',
        startup: true
      },
      tooltip: {
        isHtml: true,
        trigger: 'both'
      },
      annotations: {
        textStyle: {
          fontSize: 12,
          color: '#555'
        }
      },
      backgroundColor: 'white',
      // Remove title
      titlePosition: 'none'
    };
    
    const chartElement = document.getElementById('googleChartMedianIncome');
    if (chartElement) {
      chartElement.style.padding = '0';
      const chart = new google.visualization.LineChart(chartElement);
      
      const createCustomTooltip = function(date, areaCodeValue, nationalValue, description) {
        return `<div style="padding:10px; border:1px solid #ccc; background-color:white; border-radius:4px;">
          <strong>${date}</strong><br>
          <div style="font-size:11px; color:#666;">${description}</div>
          <div style="margin-top:5px;">
            <span style="color:#2e3192; font-weight:bold;">${areaCodeName} Area: ${formatDollar.format(areaCodeValue)}</span><br>
            <span style="color:#E24C4B; font-weight:bold;">National: ${formatDollar.format(nationalValue)}</span>
          </div>
        </div>`;
      };
      
      google.visualization.events.addListener(chart, 'ready', function() {
        const container = chartElement.getElementsByTagName('svg')[0].parentNode;
        container.onmousemove = function(e) {
          const activePoints = chart.getChartLayoutInterface().getChartAreaBoundingBox();
          if (
            e.pageX - container.getBoundingClientRect().left > activePoints.left &&
            e.pageX - container.getBoundingClientRect().left < activePoints.width + activePoints.left &&
            e.pageY - container.getBoundingClientRect().top > activePoints.top &&
            e.pageY - container.getBoundingClientRect().top < activePoints.height + activePoints.top
          ) {
            const cli = chart.getChartLayoutInterface();
            const x = cli.getXLocation(e);
            const y = cli.getYLocation(e);
            
            let closestPoint = null;
            let closestDistance = Infinity;
            
            for (let i = 0; i < dataTable.getNumberOfRows(); i++) {
              const xPos = cli.getXLocation(dataTable.getValue(i, 0));
              const yPos1 = cli.getYLocation(dataTable.getValue(i, 1));
              const yPos2 = cli.getYLocation(dataTable.getValue(i, 2));
              
              const dist1 = Math.sqrt(Math.pow(x - xPos, 2) + Math.pow(y - yPos1, 2));
              const dist2 = Math.sqrt(Math.pow(x - xPos, 2) + Math.pow(y - yPos2, 2));
              
              if (dist1 < closestDistance || dist2 < closestDistance) {
                closestDistance = Math.min(dist1, dist2);
                closestPoint = i;
              }
            }
            
            if (closestPoint !== null && closestDistance < 50) {
              const year = dataTable.getValue(closestPoint, 0);
              const areaValue = dataTable.getValue(closestPoint, 1);
              const nationalValue = dataTable.getValue(closestPoint, 2);
              
              const description = incomeData[closestPoint + 1][1];
              
              chart.setAction({
                id: 'tooltip',
                text: createCustomTooltip(year, areaValue, nationalValue, description)
              });
            }
          }
        };
      });
      
      chart.draw(dataTable, options);
      
      const lastRowIndex = dataTable.getNumberOfRows() - 1;
      if (lastRowIndex >= 0) {
        const lastYear = dataTable.getValue(lastRowIndex, 0); // eslint-disable-line no-unused-vars
        const lastValue = dataTable.getValue(lastRowIndex, 1);
        const formattedValue = formatDollar.format(lastValue);
        
        const annotationOptions = JSON.parse(JSON.stringify(options));
        annotationOptions.annotations.datum = {
          [lastRowIndex]: [{
            type: 'text',
            position: 'top',
            data: {
              row: lastRowIndex,
              column: 1
            },
            text: formattedValue
          }]
        };
        chart.draw(dataTable, annotationOptions);
      }
    }
  }

  /**
   * Creates an area chart showing population growth over time
   * HTML Element ID used:
   * - googleChartPopulationGrowth: Displays population growth over time
   */
  function drawPopulationGrowthChart() {
    if (!apiData.flourishDemographic || !apiData.flourishDemographic.byPopulation || !apiData.flourishDemographic.byPopulation.data || !apiData.flourishDemographic.byPopulation.data.data) {
      return;
    }
    
    const populationData = apiData.flourishDemographic.byPopulation.data.data;
    
    const dataTable = new google.visualization.DataTable();
    dataTable.addColumn('string', 'Year');
    dataTable.addColumn('number', 'Population');
    dataTable.addColumn({type: 'string', role: 'tooltip'});
    
    const dataStartIndex = populationData[0][0] === 'Year' ? 1 : 0;
    
    populationData.slice(dataStartIndex).forEach(row => {
      const year = row[0];
      const population = parseFloat(String(row[1]).replace(/,/g, ''));
      const tooltip = `Year: ${year}\nPopulation: ${population.toLocaleString()}`;
      
      dataTable.addRow([
        year,
        population,
        tooltip
      ]);
    });
    
    const isMobile = window.innerWidth < 768;
    
    const axisFontSize = isMobile ? 9 : 12;
    
    const options = {
      height: 400,
      width: '100%',
      legend: { position: 'none' },
      chartArea: {
        left: 60,
        right: 20, 
        top: 40,
        bottom: 80,
        width: "90%",
        height: "70%"
      },
      hAxis: {
        title: 'Year',
        slantedText: true,
        slantedTextAngle: 45,
        textStyle: {
          fontSize: axisFontSize
        },
        gridlines: {
          color: 'transparent'
        }
      },
      vAxis: {
        format: isMobile ? 'short' : '#,###,###',
        minValue: 0,
        viewWindow: { min: 0 },
        textStyle: {
          fontSize: axisFontSize
        },
        gridlines: {
          color: 'transparent'
        }
      },
      colors: ['#2e3192'], 
      areaOpacity: 0.6,
      lineWidth: 2,
      pointSize: 5,
      backgroundColor: 'white',
      animation: {
        duration: 1000,
        easing: 'out',
        startup: true
      },
      // Remove title
      titlePosition: 'none'
    };
    
    const chartElement = document.getElementById('googleChartPopulationGrowth');
    if (chartElement) {
      const chart = new google.visualization.AreaChart(chartElement);
      chart.draw(dataTable, options);
    }
  }

  /**
   * Creates three workforce statistics charts:
   * 1. Labor Force Participation Rate (blue)
   * 2. Employment to Population Ratio (red)
   * 3. Unemployment Rate (green)
   * HTML Element IDs:
   * - laborForceChart, employmentRatioChart, unemploymentRateChart
   */
  function drawWorkforceStatisticsCharts() {
    // Check if workforce data exists
    if (!apiData.flourishDemographic || !apiData.flourishDemographic.byWorkForce || !apiData.flourishDemographic.byWorkForce.data || !apiData.flourishDemographic.byWorkForce.data.data) {
      return;
    }
    
    // Get data from the API
    const workforceData = apiData.flourishDemographic.byWorkForce.data.data;
    
    const isMobile = window.innerWidth < 768;
    
    const axisFontSize = isMobile ? 9 : 11;
    
    const container = document.getElementById('workforceChartsContainer');
    if (!container) return;
    
    createWorkforceChart(
      workforceData, 
      'laborForceChart', 
      'Labor Force Participation Rate', 
      1, 
      '#2e3192', 
      '#\'%\''
    );
    
    createWorkforceChart(
      workforceData, 
      'employmentRatioChart', 
      'Employment to Population Ratio', 
      2, 
      '#E24C4B', 
      '#\'%\''
    );
    
    createWorkforceChart(
      workforceData, 
      'unemploymentRateChart', 
      'Unemployment Rate', 
      3, 
      '#71C889', 
      '#\'%\''
    );
    
    /**
     * Helper function to create a workforce statistic chart
     */
    function createWorkforceChart(data, elementId, title, dataColumnIndex, color, formatSuffix) {
      const dataTable = new google.visualization.DataTable();
      dataTable.addColumn('string', 'Year');
      dataTable.addColumn('number', title);
      
      const dataStartIndex = data[0][0] === 'Year' ? 1 : 0;
      data.slice(dataStartIndex).forEach(row => {
        const year = row[0];
        let value = row[dataColumnIndex];
        
        if (typeof value === 'string') {
          value = parseFloat(value.replace('%', ''));
        } else if (typeof value === 'number' && value < 1) {
          value = value * 100;
        }
        
        dataTable.addRow([
          year,
          value
        ]);
      });
      
      const chartElement = document.getElementById(elementId);
      if (chartElement) {
        const options = {
          fontSize: 14,
          fontWeight: "bold",
          height: 300,
          width: '100%',
          legend: { position: 'top' },
          curveType: 'function', 
          lineWidth: 4,
          pointSize: 6,
          animation: {
            duration: 1000,
            easing: 'out',
            startup: true
          },
          chartArea: {
            left: 60,
            right: 20, 
            top: 40,
            bottom: 60,
            width: "80%",
            height: "70%"
          },
          hAxis: {
            title: 'Year',
            slantedText: true,
            textStyle: {
              fontSize: axisFontSize
            }
          },
          vAxis: {
            title: '%',
            format: formatSuffix,
            gridlines: {
              count: 6
            },
            textStyle: {
              fontSize: axisFontSize
            }
          },
          colors: [color],
          backgroundColor: 'white',
          // Remove title
          titlePosition: 'none'
        };
        
        const chart = new google.visualization.LineChart(chartElement);
        chart.draw(dataTable, options);
      }
    }
  }
}

export default initDemographicCharts; 